<template>
  <div class="ip">
    <!--
      Recording navigation: the category on top, what it holds underneath.

      Everything a session contains is reachable from here, so nobody has to open a tab to
      find out what is in it. Children are icons with their label on hover, and carry their
      count in brackets wherever the column is wide enough to show it.
    -->
    <nav class="ip-nav" aria-label="Recording navigation">
      <div class="ip-grid">
        <button
          v-for="t in navTabs"
          :key="'t' + t.key"
          class="ip-tab"
          :class="{ on: tab === t.key }"
          type="button"
          @click="tab = t.key"
        >
          {{ t.label }}
        </button>

        <!--
          The column is the control, not just its label: a click anywhere in the cell picks
          the category. `.self` keeps it to the cell's own background, so the icons inside
          still do their own, narrower thing.
        -->
        <div
          v-for="t in navTabs"
          :key="'c' + t.key"
          class="ip-cell"
          :class="{ on: tab === t.key }"
          @click.self="tab = t.key"
        >
          <button
            v-for="c in t.children"
            :key="t.key + c.key"
            class="ip-ic"
            :class="{ on: tab === t.key && t.active === c.key }"
            type="button"
            :disabled="c.n === 0"
            :title="c.n == null ? c.label : `${c.label} (${c.n})`"
            :aria-label="c.n == null ? c.label : `${c.label}, ${c.n}`"
            @click="pick(t.key, c.key)"
          >
            <!-- eslint-disable-next-line vue/no-v-html -->
            <svg viewBox="0 0 24 24" aria-hidden="true" v-html="c.icon"></svg>
            <b v-if="c.n != null" class="ip-ic-n">({{ c.n }})</b>
          </button>
        </div>
      </div>
      <p class="ip-hint">Hover for labels · Select a category to filter</p>
    </nav>

    <!-- filter row (QAPO chips + search) - only where a list can be filtered -->
    <div v-show="tab === 'insights'" class="ip-filter">
      <div class="schips">
        <button
          v-for="s in sentDefs"
          :key="s.key"
          class="schip"
          :class="[
            s.key,
            { on: sFilter === s.key, none: !s.n && sFilter !== s.key },
          ]"
          type="button"
          :disabled="!s.n && sFilter !== s.key"
          :title="
            s.n
              ? `Only ${s.label.toLowerCase()} moments`
              : `No ${s.label.toLowerCase()} moments match the current filter`
          "
          @click="toggleS(s.key)"
        >
          <i class="sdot" :class="s.key"></i>{{ s.label }}<b>{{ s.n }}</b>
        </button>
      </div>
      <div class="qsearch">
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <circle cx="11" cy="11" r="7" />
          <path d="M21 21l-4.3-4.3" />
        </svg>
        <input
          v-model="search"
          type="search"
          placeholder="Search insights…"
          aria-label="Search insights"
        />
      </div>
    </div>

    <div class="ip-body">
      <!-- OVERVIEW -->
      <div v-show="tab === 'overview'" class="ip-overview">
        <template v-if="analysis.summary">
          <div class="ip-h">In one line</div>
          <p class="ip-one">{{ oneLine }}</p>
          <template v-if="restOfSummary">
            <p v-if="fullSummary" class="ip-lede">{{ restOfSummary }}</p>
            <button
              class="ip-more"
              type="button"
              @click="fullSummary = !fullSummary"
            >
              {{ fullSummary ? 'Show less' : 'Read the full summary' }}
            </button>
          </template>
        </template>

        <template v-if="(analysis.keyInsights || []).length">
          <div class="ip-h">Key insights</div>
          <ul class="ip-list">
            <li v-for="(x, i) in analysis.keyInsights" :key="'k' + i">
              <svg class="li-ic gold" viewBox="0 0 24 24" aria-hidden="true">
                <path
                  d="M9 18h6M10 21h4M12 3a6 6 0 00-4 10.5c.6.6 1 1.3 1 2.5h6c0-1.2.4-1.9 1-2.5A6 6 0 0012 3z"
                />
              </svg>
              <span>{{ x }}</span>
            </li>
          </ul>
        </template>

        <template v-if="(analysis.recommendedActions || []).length">
          <div class="ip-h">Recommended actions</div>
          <ul class="ip-list">
            <li v-for="(x, i) in analysis.recommendedActions" :key="'a' + i">
              <svg class="li-ic pos" viewBox="0 0 24 24" aria-hidden="true">
                <circle cx="12" cy="12" r="9" />
                <path d="M8.5 12l2.5 2.5L15.5 9.5" />
              </svg>
              <span>{{ x }}</span>
            </li>
          </ul>
        </template>

        <p
          v-if="!analysis.summary && !(analysis.keyInsights || []).length"
          class="ip-empty"
        >
          No written overview for this recording. The full transcript is indexed
          and searchable.
        </p>
      </div>

      <!-- HIGHLIGHTS -->
      <div v-show="tab === 'highlights'" class="ip-highlights">
        <template v-if="showClipList">
          <button
            v-for="(c, i) in clips"
            :key="'c' + i"
            class="hl-row"
            type="button"
            @click="onFind({ seconds: secOf(c.timestamp) }, -1)"
          >
            <span class="hl-thumb" :style="thumb(c.posterUrl)"></span>
            <span class="hl-main">
              <span class="hl-title">{{
                c.blurb || c.title || 'Highlight'
              }}</span>
              <span class="hl-meta"
                >{{ c.timestamp
                }}<template v-if="c.length"> · {{ c.length }}s</template
                ><template v-if="c.type">
                  · {{ typeLabel(c.type) }}</template
                ></span
              >
            </span>
            <svg class="hl-arrow" viewBox="0 0 24 24" aria-hidden="true">
              <path d="M5 12h14M13 6l6 6-6 6" />
            </svg>
          </button>
        </template>

        <div v-else-if="showShortList" class="hl-shorts">
          <ShortCard v-for="(s, i) in shorts" :key="'s' + i" :short="s" />
        </div>

        <template v-if="showCams && cams.length">
          <div class="ip-h ip-h-mt">Cameras</div>
          <div class="hl-cams">
            <CamCard v-for="(m, i) in cams" :key="'m' + i" :cam="m" />
          </div>
        </template>

        <p
          v-if="!clips.length && !shorts.length && !(showCams && cams.length)"
          class="ip-empty"
        >
          No highlights were cut from this recording. The full transcript is
          indexed and searchable.
        </p>
      </div>

      <!-- INSIGHTS -->
      <div v-show="tab === 'insights'" class="ip-insights">
        <template v-if="filtered.length">
          <button
            v-for="(it, i) in filtered"
            :key="'f' + i"
            class="ip-find"
            :class="{ now: it === nowFinding, flash: i === flashIdx }"
            type="button"
            @click="onFind(it, i)"
          >
            <div class="ipf-top">
              <span class="ipf-ts">{{ it.timestamp }}</span>
              <span class="ipf-type" :class="sent(it.sentiment)">{{
                typeLabel(it.type)
              }}</span>
              <span v-if="(it.qapo || []).length" class="ipf-qapo">
                <i v-for="k in it.qapo" :key="k">{{ k }}</i>
              </span>
            </div>
            <p class="ipf-text">{{ it.text }}</p>
            <p v-if="it.context" class="ipf-ctx">{{ it.context }}</p>
            <q v-if="hasQuote(it)">{{ it.quote }}</q>
          </button>
        </template>
        <p v-else class="ip-empty">
          Nothing here is <b>{{ activeFilterLabel }}</b> in this recording.
          <button class="ip-clear" type="button" @click="clearFilters">
            Clear filters
          </button>
        </p>
      </div>

      <!-- DOWNLOADS -->
      <div v-show="tab === 'downloads'" class="ip-downloads">
        <DownloadsTab
          :rec="rec"
          :storage="storage"
          :event-id="eventId"
          :focus="dlFocus"
        />
      </div>

      <p
        v-if="analysisVersion && (tab === 'overview' || tab === 'insights')"
        class="ip-provenance"
      >
        Analysis · {{ analysisVersion
        }}<template v-if="analysisDate">
          · generated {{ analysisDate }}</template
        >
      </p>
    </div>
  </div>
</template>

<script>
import { QAPO, sentClass, hasQuote, secOf, fmtDateShort } from '../useEvent';
import ShortCard from './ShortCard.vue';
import CamCard from './CamCard.vue';
import DownloadsTab from './DownloadsTab.vue';

const SENTS = [
  { key: 'positive', label: 'Positive' },
  { key: 'neutral', label: 'Neutral' },
  { key: 'negative', label: 'Negative' },
  { key: 'mixed', label: 'Mixed' },
];

const ICONS = {
  clips: '<rect x="3" y="5" width="18" height="12" rx="2"/><path d="M2 20h20"/>',
  shorts: '<rect x="8" y="3" width="8" height="18" rx="2"/><path d="M11 18.5h2"/>',
  video: '<rect x="3" y="6" width="12" height="12" rx="2"/><path d="M15 10.5l6-3.5v10l-6-3.5z"/>',
  music: '<path d="M9 18V6l10-2v12"/><circle cx="7" cy="18" r="2"/><circle cx="17" cy="16" r="2"/>',
  file: '<path d="M14 3H7a2 2 0 00-2 2v14a2 2 0 002 2h10a2 2 0 002-2V8z"/><path d="M14 3v5h5M9 13h6M9 17h4"/>',
  Q: '<path d="M6 17q-1 0-1-1v-2.6q0-3.2 3.2-4.4l.6 1.1Q7 11 7 12h1q1 0 1 1v3q0 1-1 1zm9 0q-1 0-1-1v-2.6q0-3.2 3.2-4.4l.6 1.1Q16 11 16 12h1q1 0 1 1v3q0 1-1 1z"/>',
  A: '<circle cx="12" cy="12" r="9"/><path d="M9.5 9.5a2.5 2.5 0 013.5-1.8c1.6.7 1.4 2.6.2 3.3-.8.5-1.2.9-1.2 1.8M12 16.5h.01"/>',
  P: '<path d="M12 3.5l9 15.5H3z"/><path d="M12 10v4M12 16.5h.01"/>',
  O: '<path d="M2 12s3.6-7 10-7 10 7 10 7-3.6 7-10 7-10-7-10-7z"/><circle cx="12" cy="12" r="2.6"/>',
};

export default {
  name: 'InsightsPanel',
  components: { ShortCard, CamCard, DownloadsTab },
  props: {
    analysis: { type: Object, default: () => ({}) },
    rec: { type: Object, required: true },
    eventId: { type: String, default: '' },
    recId: { type: String, default: '' },
    currentTime: { type: Number, default: 0 },
    showCams: { type: Boolean, default: true },
    storage: { type: Object, default: null },
    forceTab: { type: String, default: '' },
    // preset QAPO filter when arriving via a hub deep-link (?qapo=Q)
    initialQapo: { type: String, default: null },
    // preset sentiment filter when arriving from the hub's sentiment bar
    initialSentiment: { type: String, default: null },
  },
  data() {
    return {
      tab: this.forceTab || 'overview',
      qFilter: this.initialQapo || null,
      sFilter: this.initialSentiment || null,
      search: '',
      flashIdx: -1,
      hlView: 'clips',
      // which kind of file the downloads list is narrowed to, if any
      dlFocus: null,
      fullSummary: false,
    };
  },
  computed: {
    /** Everything cut from this session: what Highlights actually contains. */
    momentCount() {
      return this.clips.length + this.shorts.length;
    },
    /**
     * The navigation, in one place: each category and what it holds.
     *
     * `active` is the child currently in force, so the menu can show where you are without
     * every button working out the answer for itself.
     */
    navTabs() {
      const m = (this.rec && this.rec.media) || {};
      return [
        { key: 'overview', label: 'Overview', children: [], active: null },
        {
          key: 'highlights',
          label: 'Highlights',
          active: this.hlView,
          children: [
            this.clips.length
              ? { key: 'clips', label: 'Clips', icon: ICONS.clips, n: this.clips.length }
              : null,
            this.shorts.length
              ? { key: 'shorts', label: 'Shorts', icon: ICONS.shorts, n: this.shorts.length }
              : null,
          ].filter(Boolean),
        },
        {
          key: 'insights',
          label: 'Insights',
          active: this.qFilter,
          children: this.qapoDefs.map((q) => ({
            key: q.key,
            label: q.name,
            icon: q.icon,
            n: q.n,
          })),
        },
        {
          key: 'downloads',
          label: 'Downloads',
          active: this.dlFocus,
          children: [
            m.video ? { key: 'video', label: 'Video', icon: ICONS.video, n: null } : null,
            m.audio ? { key: 'audio', label: 'Music', icon: ICONS.music, n: null } : null,
            m.transcriptVtt
              ? { key: 'file', label: 'Transcript', icon: ICONS.file, n: null }
              : null,
          ].filter(Boolean),
        },
      ];
    },
    // the first sentence carries the session; the rest waits behind a toggle
    oneLine() {
      const t = String(this.analysis.summary || '').trim();
      const m = t.match(/^.*?[.!?](?=\s|$)/);
      return m ? m[0] : t;
    },
    restOfSummary() {
      const t = String(this.analysis.summary || '').trim();
      return t.slice(this.oneLine.length).trim();
    },
    // counts are cross-filtered: each chip shows what you would actually get if you
    // clicked it, given the other filter that is already on. Without this a chip can
    // advertise "Negative 3" and then land on an empty list.
    qapoDefs() {
      const pool = this.sFilter
        ? this.findings.filter(
            (f) =>
              String(f.sentiment || 'neutral').toLowerCase() === this.sFilter
          )
        : this.findings;
      return QAPO.map((q) => ({
        ...q,
        icon: ICONS[q.key],
        n: pool.filter((f) => (f.qapo || []).indexOf(q.key) >= 0).length,
      }));
    },
    sentDefs() {
      const pool = this.qFilter
        ? this.findings.filter((f) => (f.qapo || []).indexOf(this.qFilter) >= 0)
        : this.findings;
      // a band the recording has at all stays listed, even when the current kind
      // filter empties it - it just renders as unavailable
      return SENTS.map((x) => ({
        ...x,
        n: pool.filter(
          (f) => String(f.sentiment || 'neutral').toLowerCase() === x.key
        ).length,
        total: this.findings.filter(
          (f) => String(f.sentiment || 'neutral').toLowerCase() === x.key
        ).length,
      })).filter((x) => x.total > 0);
    },
    activeFilterLabel() {
      const bits = [];
      if (this.qFilter) {
        const m = QAPO.find((q) => q.key === this.qFilter);
        bits.push(m ? m.name.toLowerCase() : this.qFilter);
      }
      if (this.sFilter) bits.push(this.sFilter);
      return bits.join(' + ');
    },
    els() {
      return this.rec.elements || {};
    },
    clips() {
      return this.els.clips || [];
    },
    shorts() {
      return this.els.shorts || [];
    },
    showClipList() {
      return (
        this.clips.length && (this.hlView === 'clips' || !this.shorts.length)
      );
    },
    showShortList() {
      return (
        this.shorts.length && (this.hlView === 'shorts' || !this.clips.length)
      );
    },
    cams() {
      return this.els.cams || [];
    },
    findings() {
      return this.analysis.findings || [];
    },
    analysisVersion() {
      return this.analysis.version || null;
    },
    analysisDate() {
      const g = this.analysis.generatedAt;
      if (!g) return '';
      const t = Date.parse(g);
      return isNaN(t) ? g : fmtDateShort(t);
    },
    nowFinding() {
      const ct = this.currentTime;
      let ans = null;
      let best = -1;
      this.findings.forEach((f) => {
        if (
          typeof f.seconds === 'number' &&
          f.seconds <= ct &&
          f.seconds > best
        ) {
          best = f.seconds;
          ans = f;
        }
      });
      return ans;
    },
    filtered() {
      const q = this.search.trim().toLowerCase();
      return this.findings.filter((it) => {
        if (this.qFilter && (it.qapo || []).indexOf(this.qFilter) < 0)
          return false;
        if (
          this.sFilter &&
          String(it.sentiment || 'neutral').toLowerCase() !== this.sFilter
        )
          return false;
        if (q) {
          const hay = `${it.text || ''}${it.context || ''}${
            it.quote || ''
          }`.toLowerCase();
          if (hay.indexOf(q) < 0) return false;
        }
        return true;
      });
    },
  },
  watch: {
    forceTab(t) {
      if (t) this.tab = t;
    },
  },
  methods: {
    /**
     * Go straight to a child, whichever category holds it.
     *
     * Choosing the one already in force clears it, so a filter can be undone where it was
     * applied rather than hunting for a reset.
     */
    pick(tabKey, childKey) {
      const already = this.tab === tabKey;
      if (tabKey === 'highlights') {
        this.hlView = childKey;
      } else if (tabKey === 'insights') {
        this.qFilter = already && this.qFilter === childKey ? null : childKey;
      } else if (tabKey === 'downloads') {
        this.dlFocus = already && this.dlFocus === childKey ? null : childKey;
      }
      this.tab = tabKey;
    },
    sent: sentClass,
    hasQuote,
    secOf,
    typeLabel(t) {
      return String(t || '').replace(/_/g, ' ');
    },
    thumb(url) {
      return url ? { backgroundImage: `url("${url}")` } : {};
    },
    toggleS(k) {
      this.sFilter = this.sFilter === k ? null : k;
    },
    clearFilters() {
      this.qFilter = null;
      this.sFilter = null;
      this.search = '';
    },
    onFind(it, i) {
      if (i >= 0) {
        this.flashIdx = i;
        clearTimeout(this._ft);
        this._ft = setTimeout(() => {
          this.flashIdx = -1;
        }, 600);
      }
      this.$emit('seek', it.seconds);
    },
  },
  beforeDestroy() {
    clearTimeout(this._ft);
  },
};
</script>
